import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  borderColors,
  ColorsConstants,
  inputColor,
} from "../cssUtils/constants";
import typography from "../cssUtils/typography";
import { useContext } from "react";
import { Box, Button, IconButton } from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { MuiPickersAdapterContext } from "@mui/x-date-pickers/LocalizationProvider";

// Header with separate month / year buttons: clicking the month opens the month view, the year opens the year view.
const MonthYearHeader = ({ currentMonth, views, view, onViewChange, onMonthChange, minDate, maxDate, disableFuture, disablePast, disabled, labelId }) => {
  const { utils } = useContext(MuiPickersAdapterContext);
  const prev = utils.addMonths(currentMonth, -1);
  const next = utils.addMonths(currentMonth, 1);
  const today = utils.startOfMonth(utils.date());
  const prevDisabled =
    disabled ||
    (minDate && utils.isBefore(prev, utils.startOfMonth(minDate))) || (disablePast && utils.isBefore(prev, today));
  const nextDisabled =
    disabled ||
    (maxDate && utils.isAfter(next, utils.startOfMonth(maxDate))) || (disableFuture && utils.isAfter(next, today));
  const labelSx = (v) => ({
    ...typography.bodyLarge,
    fontWeight: view === v ? 600 : 500,
    color: inputColor,
    textTransform: "none",
    minWidth: 0,
    px: 1,
  });

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, pt: 2, pb: 1 }}>
      <IconButton size="small" aria-label="Previous month" disabled={prevDisabled} onClick={() => onMonthChange(prev, "right")}>
        <ChevronLeft />
      </IconButton>
      <Box id={labelId} aria-live="polite">
        {views.includes("month") && (
          <Button size="small" sx={labelSx("month")} disabled={disabled} onClick={() => onViewChange?.(view === "month" ? "day" : "month")}>
            {utils.format(currentMonth, "month")}
          </Button>
        )}
        {views.includes("year") && (
          <Button size="small" sx={labelSx("year")} disabled={disabled} onClick={() => onViewChange?.(view === "year" ? "day" : "year")}>
            {utils.format(currentMonth, "year")}
          </Button>
        )}
      </Box>
      <IconButton size="small" aria-label="Next month" disabled={nextDisabled} onClick={() => onMonthChange(next, "left")}>
        <ChevronRight />
      </IconButton>
    </Box>
  );
};

const AppDatePicker = ({
  value,
  onChange,
  minDate,
  disableFuture = false,
  error = false,
  helperText = "",
  placeholder = "DD/MM/YYYY",
  fullWidth = true,
  views = ["year", "month", "day"],
  format = "DD/MM/YYYY", // dayjs-style tokens; pass format="dd/MM/yyyy" when using AdapterDateFns
  slotProps: externalSlotProps = {}, // ← extract slotProps separately
  slots: externalSlots,
  ...props
}) => {
  return (
    <DatePicker
      value={value}
      onChange={onChange}
      minDate={minDate}
      disableFuture={disableFuture}
      format={format}
      views={views}
      openTo={props.openTo ?? (views.includes("day") ? "day" : "month")}
      slots={views.includes("day") ? { calendarHeader: MonthYearHeader, ...externalSlots } : externalSlots}
      slotProps={{
        ...externalSlotProps, // ← spread external ones first
        textField: {
          ...externalSlotProps.textField, // ← merge textField overrides
          size: "small",
          error,
          helperText,
          placeholder,
          sx: {
            ...typography.bodyLarge,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              height: 40,
              color: inputColor,
              ...typography.bodyLarge,
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "8px",
                borderColor: borderColors.inputFieldGrey,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: ColorsConstants.primaryGray50,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: ColorsConstants.primaryGray100,
              },
            },

            width: fullWidth ? "100%" : "fit-content",
          },
          InputProps: {
            sx: {
              height: 40,
              "& input": {
                padding: "8px 14px !important",
                boxSizing: "border-box",
                color: inputColor,
                ...typography.bodyLarge,
              },
              "& input::placeholder": {
                color: inputColor,
                opacity: 1,
              },
              "& .MuiInputAdornment-root": {
                height: "100%",
              },
              "& .MuiIconButton-root": {
                padding: "6px",
              },
            },
          },
        },
      }}
      {...props}
    />
  );
};

export default AppDatePicker;
